import { BreakpointObserver } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  Type,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
  HeaderFooterService,
  NavigationBreadcrumbExpansion,
  NavigationBreadcrumbSelection,
  SnackbarService,
} from '@shared/ui';
import {
  AnalysisStateStore,
  AnalystStore,
  AssetStore,
  FindingStore,
  VideoRoiStore,
} from '../../stores';
import { RoiStatsEffects } from '../../effects';
import { DrawingMode, ViewerMode } from '../../types/viewer.types';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { filter, map, pairwise, shareReplay, startWith, take, takeUntil } from 'rxjs/operators';
import { NewClassDialogComponent } from '../dialogs/new-class-dialog/new-class-dialog.component';
import { ConfirmationDialogComponent } from '@shared/ui';
import { PresetFilters } from '../../stores';
import { VideoROIOverlay } from '@shared/viewers/feature';
import { BreadcrumbNavigationEffects } from '../../effects';
import { NavigationStore } from '../../stores';
import {
  AI_ANALYSIS_READY,
  AI_ANALYSIS_SAVE_BLOCKED_I18N,
  SampleAiAnalysisProgressStore,
} from '../microscope-image/state';
import { MicroscopeVideoFacade } from './microscope-video.facade';
import { LabelStore, MethodTypeStore, PipelineStore, ProtocolStore, StepStore } from '@telespot/protocols/ui';
import { MicroLayoutStore, RoiStore, RoiStatsStore, MosaicPaginationStore } from '../../stores';
import { Analyst, IAssetItem, IState } from '@analysis/domain';
import { AuthService, FeaturesService } from '@telespot/web-core';
import Parse from 'parse';
import { DOWNLOAD_ASSETS_DIALOG_COMPONENT } from '../../tokens/component.tokens';

@Component({
  selector: 'ts-microscope-video',
  templateUrl: './microscope-video.component.html',
  styleUrls: ['./microscope-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    MicroscopeVideoFacade,
    AssetStore,
    NavigationStore,
    BreadcrumbNavigationEffects,
    SampleAiAnalysisProgressStore,
    ProtocolStore,
    PipelineStore,
    StepStore,
    LabelStore,
    MethodTypeStore,
    MicroLayoutStore,
    VideoRoiStore,
    AnalysisStateStore,
    FindingStore,
    AnalystStore,
    RoiStore,
    RoiStatsEffects,
    RoiStatsStore,
    MosaicPaginationStore,
  ],
  standalone: false,
})
export class MicroscopeVideoComponent implements OnInit, OnDestroy {
  @ViewChild('scrollableWrapper') scrollableWrapper!: ElementRef;
  @ViewChild('rulerTrigger') rulerTrigger?: MatMenuTrigger;
  @ViewChild('filtersTrigger') filtersTrigger?: MatMenuTrigger;

  // Exposed for the inlined viewer-tools / drawing actions in the top bar's center zone.
  public readonly ViewerMode = ViewerMode;
  public readonly DrawingMode = DrawingMode;

  private _destroy$ = new Subject<void>();
  private _showMobileAnalysisPanelB$ = new BehaviorSubject<boolean>(true);

  public readonly isIOS = this._platform.IOS;
  public readonly layout$: Observable<'mobile' | 'web'> = combineLatest([
    this._breakpoint.observe(['(min-width: 600px)']),
  ]).pipe(
    map(([breakpoint]) => breakpoint.matches),
    startWith(!(this._platform.ANDROID || this._platform.IOS)),
    map<boolean, 'web' | 'mobile'>((web) => (web ? 'web' : 'mobile')),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly showMobileAnalysisPanel$ = this._showMobileAnalysisPanelB$.asObservable();
  public readonly aiPollingExhausted$ = this.sampleAiProgressStore.pollingExhausted$;

  get isMobile(): boolean {
    return this._platform.IOS || this._platform.ANDROID;
  }

  constructor(
    @Inject(Platform) private _platform: Platform,
    private _breakpoint: BreakpointObserver,
    private _router: Router,
    private _route: ActivatedRoute,
    private renderer: Renderer2,
    private _headerFooterService: HeaderFooterService,
    public readonly authService: AuthService,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private readonly snackbarService: SnackbarService,
    private readonly sampleAiProgressStore: SampleAiAnalysisProgressStore,
    public readonly micro: MicroscopeVideoFacade,
    public readonly featureService: FeaturesService,
    @Inject(DOWNLOAD_ASSETS_DIALOG_COMPONENT) private readonly downloadAssetsDialog: Type<unknown>
  ) {
    this._headerFooterService.toggleHeaderAndFooter(false);
  }

  @HostListener('document:keydown.meta.s', ['$event'])
  @HostListener('document:keydown.control.s', ['$event'])
  public onSaveShortcut(event: KeyboardEvent) {
    this.micro.syncChanges();
    event.preventDefault();
    event.stopPropagation();
  }

  ngOnInit(): void {
    this.micro.saveBlockedByAiAnalysis$.pipe(takeUntil(this._destroy$)).subscribe(() =>
      this.snackbarService.showWithBoldLead(AI_ANALYSIS_SAVE_BLOCKED_I18N.title, AI_ANALYSIS_SAVE_BLOCKED_I18N.body, {
        duration: 12_000,
      })
    );

    this.sampleAiProgressStore.aiAnalysisCompleted$
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this.snackbarService.show(AI_ANALYSIS_READY, { duration: 6000 }));

    // Show success snackbar when sync completes
    this.micro.syncing$
      .pipe(
        pairwise(),
        filter(([prevSyncing, currentSyncing]) => prevSyncing && !currentSyncing),
        takeUntil(this._destroy$)
      )
      .subscribe(() => {
        this.openSnackbar('analysis.changes_saved_successfully');
      });
  }

  ngOnDestroy(): void {
    this.micro.saveProgress();

    this._headerFooterService.toggleHeaderAndFooter(true);

    this._destroy$.next();
    this._destroy$.complete();
  }

  private openSnackbar(message: string) {
    this.snackbarService.show(message);
  }

  public onToogleActiveMode(mode: ViewerMode): void {
    this.micro.setViewerMode(mode);
    if (mode === ViewerMode.drawing) this.micro.clearSelectedVideoRois();
  }

  public onToogleReference(isCurrentUser: boolean) {
    if (!isCurrentUser) return;
    this.micro.setReference();
  }

  public onNewLabel() {
    const dialogRef = this._dialog.open(NewClassDialogComponent, {
      hasBackdrop: true,
      disableClose: true,
      width: '500px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$), take(1))
      .subscribe((result) => {
        if (!result) return;

        //TODO: Pending include custom label functionality
      });
  }

  public navigate(caseId: string, sampleId: string) {
    this._router.navigate([`analyze/cases/${caseId}/videos/${sampleId}`], { queryParams: {}, replaceUrl: true });
  }

  /**
   * Breadcrumb commit. Video is "visualize only": there is no analyst level, so the sample is the
   * breadcrumb's only leaf; every level above it expands instead (see the shared component).
   *
   * - Sample: the destination case comes from the selection PATH (a cascaded sample can belong to
   *   another case), falling back to the open case.
   */
  public onBreadcrumbSelect(selection: NavigationBreadcrumbSelection, currentCaseId: string | undefined): void {
    if (selection.levelKey === 'sample') {
      const caseId = this.stepFromPath(selection, 'case') ?? currentCaseId;
      this.onSelectSample(selection.optionId, caseId);
    }
  }

  private stepFromPath(selection: NavigationBreadcrumbSelection, levelKey: string): string | undefined {
    return selection.path?.find((step) => step.levelKey === levelKey)?.optionId;
  }

  /** Breadcrumb sample commit: route to the right viewer (videos vs images) based on the
   *  sample's real asset type. Video is "visualize only", so no `historyId` is carried. */
  public onSelectSample(sampleId: string, currentCaseId: string | undefined): void {
    if (!sampleId || !currentCaseId) return;
    this.micro.navigateToSample(currentCaseId, sampleId);
  }

  /** Breadcrumb level dropdown opened: lazily load the open sample's analysts and their states. */
  public onBreadcrumbLevelOpened(levelKey: string, sampleId: string | undefined, caseId: string | undefined): void {
    if (levelKey !== 'analyst' || !sampleId || !caseId) return;
    this.micro.loadAnalysts(sampleId);
    this.micro.loadAnalysisStates(sampleId, caseId);
  }

  /** A cascading option was opened: load the level below it (workspace → case → sample). */
  public onBreadcrumbOptionExpanded(expansion: NavigationBreadcrumbExpansion): void {
    if (expansion.levelKey === 'workspace') this.micro.loadCasesForWorkspace(expansion.optionId);
    if (expansion.levelKey === 'case') this.micro.loadSamplesForCase(expansion.optionId);
  }

  public getImageFiltersApplied(imageFilters): boolean {
    return imageFilters.some((f) => f.value !== 0);
  }

  /** RESET the ruler back to its initial state and close the ruler popover. */
  public onRulerReset(): void {
    this.micro.resetRuler();
    this.rulerTrigger?.closeMenu();
  }

  /** RESET the image filters back to their initial state and close the filters popover. */
  public onFiltersReset(): void {
    this.micro.resetImageFilters();
    this.filtersTrigger?.closeMenu();
  }

  public onUpdateImageFiltersBulk(data: PresetFilters) {
    this.micro.setSelectedPreset(data.preset);
    this.micro.updateImageFiltersBulk(data.filters);
  }

  public navigateToUserAnalysis(isOwn: boolean, historyId: string): void {
    this._router.navigate([], {
      queryParams: { historyId: isOwn ? undefined : historyId },
      queryParamsHandling: 'merge',
    });
  }

  public onAssetChanged(assetId: string) {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { asset: assetId },
      queryParamsHandling: 'merge',
    });
  }

  public onAnalystChange(analyst: Analyst): void {
    const isOwn = this.authService.currentUser.id === analyst.id;
    this.navigateToUserAnalysis(isOwn, analyst.historyId);
  }

  public exit() {
    this.micro.unsynced$.pipe(take(1)).subscribe((unsynced) => {
      if (unsynced) {
        this.openUnsavedChangesDialog();
      } else {
        this.micro.navigateToCaseDetail();
      }
    });
  }

  public onDiscardChanges() {
    this.openDiscardChangesDialog();
  }

  private openDiscardChangesDialog() {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.discard_changes_prompt',
        cancelButtonText: 'button.cancel',
        acceptButtonText: 'button.discard_changes',
        defaultAction: 'cancel',
      },
      disableClose: true,
      hasBackdrop: true,
      panelClass: ['rounded'],
    };

    this._dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => result === 'accept' || result === 'default')
      )
      .subscribe(() => {
        this.micro.discardChanges();
      });
  }

  private openUnsavedChangesDialog() {
    const dialogConfig = {
      data: {
        title: '',
        text: 'info.exit_without_saving_prompt',
        cancelButtonText: 'button.cancel',
        acceptButtonText: 'button.save',
        dontSaveButtonText: 'button.continue',
        defaultAction: 'accept',
      },
      disableClose: true,
      hasBackdrop: true,
      panelClass: ['rounded'],
    };

    const onDialogClose = this._dialog
      .open(ConfirmationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        take(1),
        filter((answer) => answer !== 'cancel')
      );

    onDialogClose
      .pipe(filter((answer: string) => answer === 'discard'))
      .subscribe(() => this.micro.navigateToCaseDetail());

    onDialogClose
      .pipe(
        filter((answer: string) => answer === 'accept'),
        concatLatestFrom(() => this.micro.sampleId$)
      )
      .subscribe(([, sampleId]) => {
        const currentAnalystId = Parse.User.current()?.id;
        if (sampleId && currentAnalystId) {
          this.micro.syncChanges();

          if (!this.authService.currentUser.isOnlyGuest) {
            this.micro.updateCaseState(IState.INPROGRES);
            this.micro.updateSampleState(IState.INPROGRES);
          }
          this.micro.navigateToCaseDetail();
        }
      });
  }

  public imageFiltersApplied(imageFilters) {
    return imageFilters.some((f) => f.value !== 0);
  }

  public onSave() {
    this.micro.isReviewingOtherAnalyst$.pipe(take(1)).subscribe((isReviewingOtherAnalyst) => {
      if (isReviewingOtherAnalyst) {
        // TODO: Add review dialog if needed
      } else {
        this.micro.syncChanges();

        if (this.authService.currentUser.isOnlyGuest) return;
        this.micro.updateCaseState(IState.INPROGRES);
        this.micro.updateSampleState(IState.INPROGRES);
      }
    });
  }

  public saveAndExit() {
    this.micro.isReviewingOtherAnalyst$.pipe(take(1)).subscribe((isReviewingOtherAnalyst) => {
      if (isReviewingOtherAnalyst) {
        // TODO: Add review dialog if needed
        this.micro.navigateToCaseDetail();
      } else {
        this.micro.syncChanges();

        // Wait for sync to complete before navigating
        this.micro.syncing$
          .pipe(
            pairwise(),
            filter(([prevSyncing, currentSyncing]) => prevSyncing && !currentSyncing),
            take(1)
          )
          .subscribe(() => {
            if (!this.authService.currentUser.isOnlyGuest) {
              this.micro.updateCaseState(IState.INPROGRES);
              this.micro.updateSampleState(IState.INPROGRES);
            }
            this.micro.navigateToCaseDetail();
          });
      }
    });
  }

  public downloadAssets(imageFormat: boolean, refStripItems: IAssetItem[], sampleName) {
    this._dialog.open(this.downloadAssetsDialog, {
      hasBackdrop: true,
      disableClose: true,
      width: '800px',
      data: {
        assets: refStripItems.map((item) => {
          return {
            id: item.id,
            thumbnail: item.thumbnail,
            selected: false,
            index: item.index,
            assetFile: item.url,
            data: { hspan: item?.hspan },
          };
        }),
        sampleName,
        imageFormat,
      },
    });
  }

  public onDownloadAsset() {
    this._snackBar.open('Wait until download completes', null, {
      duration: 2000,
    });
  }

  videoBarResized() {
    const wrapperElement = this.scrollableWrapper?.nativeElement;

    if (wrapperElement) {
      this.renderer.setStyle(wrapperElement, 'overflow-y', 'auto');
    }
  }

  public onVideoROIAdded(videoRoi: Partial<VideoROIOverlay>) {
    this.micro.addRois({ overlay: videoRoi });
  }

  onROITimeUpdated(roiInfo: { roi: { id: string }; changes: { t1: number; t2: number } }) {
    this.micro.updateVideoRoiTime(roiInfo.roi.id, { t1: roiInfo.changes.t1, t2: roiInfo.changes.t2 });
  }

  //#region Mobile Panel Methods

  public closeMobileAnalysisPanel() {
    this._showMobileAnalysisPanelB$.next(false);
  }
  //#endregion
}
